import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { usePrivy } from "@privy-io/react-auth";
import { useAuth } from "@/hooks/useAuth";
import { useUSDCBalance } from "@/hooks/useUSDCBalance";
import { useChallenge } from "@/hooks/useChallenge";
import { StickyHeader } from "@/components/ui/StickyHeader";
import { ProfileStatCard } from "@/components/ProfileStatCard";
import { AchievementBadge } from "@/components/AchievementBadge";
import { ProfileChallengeCard } from "@/components/ProfileChallengeCard";
import { MonthlyActivityChart } from "@/components/MonthlyActivityChart";
import { WalletBalanceCard } from "@/components/WalletBalanceCard";
import { DepositModal } from "@/components/DepositModal";
import { WithdrawModal } from "@/components/WithdrawModal";
import { EditProfileModal } from "@/components/EditProfileModal";
import { ContactSupportModal } from "@/components/ContactSupportModal";
import { getUserAvatarUrl } from "@/utils/avatar";
import {
  getTimeLeft,
  calculateProgress,
  getChallengeIcon,
} from "@/utils/challengeHelpers";
import { homeApi, MonthStats, UserStats } from "@/api/home";

interface Achievement {
  id: string;
  icon: string;
  label: string;
  unlocked: boolean;
}

export function Profile() {
  const { user, logout, privyUser } = useAuth();
  const { exportWallet } = usePrivy();
  const navigate = useNavigate();
  const [showSettings, setShowSettings] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);
  const settingsButtonRef = useRef<HTMLButtonElement>(null);

  // Get USDC balance from blockchain
  const {
    balance: walletBalance,
    isLoading: balanceLoading,
    refetch: refetchBalance,
  } = useUSDCBalance();

  // Get active challenges from store
  const {
    myActiveChallenges,
    fetchMyActiveChallenges,
    isLoading: challengesLoading,
  } = useChallenge();

  // Dashboard data (month stats + user stats)
  const [monthStats, setMonthStats] = useState<MonthStats | null>(null);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);

  // Track if we've already fetched to prevent duplicate calls
  const hasFetchedChallengesRef = useRef(false);
  const hasFetchedDashboardRef = useRef(false);

  // Fetch active challenges on mount
  useEffect(() => {
    if (!hasFetchedChallengesRef.current) {
      hasFetchedChallengesRef.current = true;
      fetchMyActiveChallenges();
    }
  }, [fetchMyActiveChallenges]);

  // Fetch dashboard data for monthStats and userStats
  useEffect(() => {
    if (!hasFetchedDashboardRef.current) {
      hasFetchedDashboardRef.current = true;
      homeApi
        .getDashboard()
        .then((data) => {
          setMonthStats(data.monthStats);
          setUserStats(data.stats);
        })
        .catch((error) => {
          console.error("Failed to fetch dashboard data:", error);
        })
        .finally(() => {
          setDashboardLoading(false);
        });
    }
  }, []);

  // Close settings dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Don't close if clicking on the settings button (let the button's onClick handle toggle)
      if (settingsButtonRef.current?.contains(target)) {
        return;
      }
      // Close if clicking outside the dropdown
      if (settingsRef.current && !settingsRef.current.contains(target)) {
        setShowSettings(false);
      }
    };

    if (showSettings) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showSettings]);

  if (!user) {
    return null;
  }

  // Stats from dashboard API (with fallbacks)
  const stats = {
    challengesWon: userStats?.challengesWon ?? 0,
    totalEarned: userStats?.totalEarnings ?? 0,
    currentStreak: userStats?.currentStreak ?? 0,
    activeChallenges: myActiveChallenges.length,
  };

  // Mock achievements - in production these would come from API
  const achievements: Achievement[] = [
    {
      id: "1",
      icon: "local_fire_department",
      label: "Streak Starter",
      unlocked: true,
    },
    { id: "2", icon: "calendar_month", label: "Perfect Week", unlocked: true },
    {
      id: "3",
      icon: "military_tech",
      label: "First Challenge",
      unlocked: true,
    },
    { id: "4", icon: "paid", label: "High Roller", unlocked: false },
    {
      id: "5",
      icon: "social_leaderboard",
      label: "Top 10 Finisher",
      unlocked: false,
    },
  ];

  const handleChallengeClick = (challengeId: string) => {
    navigate(`/my-challenge/${challengeId}`);
  };

  // const handleShare = () => {
  //   // TODO: Implement share functionality
  // };

  const handleEditProfile = () => {
    setShowEditProfileModal(true);
  };

  const handleDeposit = () => {
    setShowDepositModal(true);
  };

  const handleWithdraw = () => {
    setShowWithdrawModal(true);
  };

  return (
    <div className="relative flex h-auto min-h-screen w-full flex-col bg-background-light dark:bg-background-dark pb-20 md:pb-8">
      <StickyHeader
        title="Profile"
        leftContent={
          <button
            ref={settingsButtonRef}
            onClick={() => setShowSettings(!showSettings)}
            className="flex cursor-pointer items-center justify-center rounded-full h-10 w-10 bg-transparent text-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
          >
            <span className="material-symbols-outlined text-2xl">settings</span>
          </button>
        }
        // rightContent={
        //   <button
        //     onClick={handleShare}
        //     className="flex cursor-pointer items-center justify-center rounded-full h-10 w-10 bg-transparent text-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
        //   >
        //     <span className="material-symbols-outlined text-2xl">share</span>
        //   </button>
        // }
      >
        {/* Settings Dropdown */}
        {showSettings && (
          <div
            ref={settingsRef}
            className="absolute top-full left-4 z-40 mt-1 bg-card-light dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-white/10 p-2 min-w-[200px]"
          >
            <button
              onClick={() => {
                setShowContactModal(true);
                setShowSettings(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 text-gray-900 dark:text-white font-medium transition-colors"
            >
              <span className="material-symbols-outlined text-xl">
                support_agent
              </span>
              Contact Us
            </button>
            <button
              onClick={async () => {
                const walletAddress = privyUser?.wallet?.address;
                if (walletAddress) {
                  await exportWallet({ address: walletAddress });
                }
                setShowSettings(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 text-gray-900 dark:text-white font-medium transition-colors"
            >
              <span className="material-symbols-outlined text-xl">key</span>
              Export Wallet
            </button>
            <button
              onClick={logout}
              className="w-full flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 text-red-600 dark:text-red-400 font-medium transition-colors"
            >
              <span className="material-symbols-outlined text-xl">logout</span>
              Logout
            </button>
          </div>
        )}
      </StickyHeader>

      {/* Profile Header */}
      <div className="flex p-4">
        <div className="flex w-full flex-col gap-4 items-center">
          <div className="flex gap-4 flex-col items-center">
            {/* Avatar - uses DiceBear generated avatar as fallback */}
            <img
              src={getUserAvatarUrl(user.avatarUrl, user.walletAddress || user.email, "bottts", 128)}
              alt={`${user.username || "User"}'s avatar`}
              className="w-32 h-32 rounded-full border-2 border-primary object-cover bg-gray-200 dark:bg-gray-700"
            />
            <div className="flex flex-col items-center justify-center">
              <p className="text-gray-900 dark:text-white text-[22px] font-bold leading-tight tracking-[-0.015em] text-center">
                {user.username || "User"}
              </p>
              <p className="text-gray-500 dark:text-gray-400 text-base font-normal leading-normal text-center">
                @{user.email?.split("@")[0] || "user"}
              </p>
            </div>
          </div>
          <button
            onClick={handleEditProfile}
            className="flex min-w-[84px] cursor-pointer items-center justify-center overflow-hidden rounded-full h-10 px-6 bg-primary/20 dark:bg-primary/20 text-primary dark:text-primary text-sm font-bold leading-normal tracking-[0.015em] w-full max-w-[480px] hover:bg-primary/30 transition-colors gap-2"
          >
            <span className="material-symbols-outlined text-lg">edit</span>
            <span className="truncate">Edit Profile</span>
          </button>
        </div>
      </div>

      {/* Profile Stats */}
      <div className="flex flex-col gap-3 px-4 py-3">
        <div className="flex gap-3">
          <ProfileStatCard value={stats.challengesWon} label="Challenges Won" />
          <ProfileStatCard value={`$${stats.totalEarned}`} label="Total Earned" />
        </div>
        <ProfileStatCard value={stats.currentStreak} label="Current Streak" className="flex-none" />
      </div>

      {/* Wallet Balance Card */}
      <div className="px-4 py-3">
        <WalletBalanceCard
          balance={walletBalance}
          isLoading={balanceLoading}
          onDeposit={handleDeposit}
          onWithdraw={handleWithdraw}
          onRefresh={refetchBalance}
        />
      </div>

      {/* Monthly Activity Chart */}
      <div className="flex flex-wrap gap-4 px-4 py-3">
        {dashboardLoading ? (
          <div className="flex w-full justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <MonthlyActivityChart
            activeDays={monthStats?.activeDays ?? 0}
            changePercentage={monthStats?.changePercentage ?? 0}
            weeklyData={monthStats?.weeklyData}
          />
        )}
      </div>

      {/* Achievements Section */}
      <h2 className="text-gray-900 dark:text-white text-[22px] font-bold leading-tight tracking-[-0.015em] px-4 pb-3 pt-5">
        Achievements
      </h2>
      <div className="flex w-full overflow-x-auto px-4 pb-4 scrollbar-hide">
        <div className="flex flex-nowrap gap-4">
          {achievements.map((achievement) => (
            <AchievementBadge
              key={achievement.id}
              icon={achievement.icon}
              label={achievement.label}
              unlocked={achievement.unlocked}
            />
          ))}
        </div>
      </div>

      {/* Active Challenges Section */}
      <h2 className="text-gray-900 dark:text-white text-[22px] font-bold leading-tight tracking-[-0.015em] px-4 pb-3 pt-5">
        Active Challenges
      </h2>
      <div className="flex flex-col gap-3 px-4 pb-8">
        {challengesLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : myActiveChallenges.length > 0 ? (
          myActiveChallenges.map((challenge) => (
            <ProfileChallengeCard
              key={challenge.id}
              icon={getChallengeIcon(challenge.type)}
              name={challenge.name}
              daysRemaining={getTimeLeft(challenge.endDate)}
              progress={calculateProgress(challenge)}
              onClick={() => handleChallengeClick(challenge.id)}
            />
          ))
        ) : (
          <div className="flex items-center justify-center rounded-xl border border-gray-200 dark:border-white/10 bg-card-light dark:bg-black/20 p-8">
            <p className="text-gray-500 dark:text-gray-400 text-center">
              No active challenges. Join a challenge to get started!
            </p>
          </div>
        )}
      </div>

      {/* Deposit Modal */}
      <DepositModal
        isOpen={showDepositModal}
        onClose={() => setShowDepositModal(false)}
        walletAddress={privyUser?.wallet?.address || ""}
        onSuccess={refetchBalance}
      />

      {/* Withdraw Modal */}
      <WithdrawModal
        isOpen={showWithdrawModal}
        onClose={() => setShowWithdrawModal(false)}
        balance={walletBalance}
        onSuccess={refetchBalance}
      />

      {/* Edit Profile Modal */}
      <EditProfileModal
        isOpen={showEditProfileModal}
        onClose={() => setShowEditProfileModal(false)}
      />

      {/* Contact Support Modal */}
      <ContactSupportModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
      />
    </div>
  );
}
